/**
 * Chat WebSocket Context
 * Manages WebSocket connection for real-time chat and presence
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import api from '../config/api';

interface UserPresence {
  userId: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  customStatus?: string;
  statusEmoji?: string;
  lastSeen?: string;
  lastActivity?: string;
}

interface ChatWebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  presence: Record<string, UserPresence>;
  connect: () => void;
  disconnect: () => void;
  updateStatus: (status: 'online' | 'away' | 'busy' | 'offline', autoStatus?: boolean) => void;
  sendMessage: (channelId: string, text: string, replyTo?: string, attachmentUrl?: string, attachmentName?: string, attachmentSize?: number) => Promise<void>;
  startTyping: (channelId: string) => void;
  stopTyping: (channelId: string) => void;
  on: (event: string, handler: (data: any) => void) => () => void;
  emit: (event: string, data: any) => void;
}

const ChatWebSocketContext = createContext<ChatWebSocketContextType>({
  socket: null,
  isConnected: false,
  presence: {},
  connect: () => {},
  disconnect: () => {},
  updateStatus: () => {},
  sendMessage: async () => { throw new Error('Not implemented'); },
  startTyping: () => {},
  stopTyping: () => {},
  on: () => () => {},
  emit: () => {},
});

export const useChatWebSocket = () => {
  const context = useContext(ChatWebSocketContext);
  if (!context) {
    throw new Error('useChatWebSocket must be used within ChatWebSocketProvider');
  }
  return context;
};

interface ChatWebSocketProviderProps {
  children: React.ReactNode;
}

export const ChatWebSocketProvider: React.FC<ChatWebSocketProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [presence, setPresence] = useState<Record<string, UserPresence>>({});
  const socketRef = useRef<Socket | null>(null);
  const listenersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (socketRef.current?.connected || !user) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const isProduction = window.location.hostname === 'vitreous.support' 
      || window.location.hostname.includes('vitreous.support')
      || window.location.hostname === 'patelhrms.com' 
      || window.location.hostname.includes('patelhrms.com');
    
    const socketUrl = isProduction ? window.location.origin : 'http://localhost:5000';

    console.log('[Chat WS] Connecting to:', `${socketUrl}/chat`);

    const newSocket = io(`${socketUrl}/chat`, {
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    newSocket.on('connect', () => {
      console.log('[Chat WS] ✅ Connected');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('[Chat WS] ❌ Disconnected');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('[Chat WS] Connection error:', error.message);
      setIsConnected(false);
    });

    // Handle presence updates
    newSocket.on('presence:update', (data: { userId: string; status: string; timestamp: string }) => {
      setPresence(prev => ({
        ...prev,
        [data.userId]: {
          ...prev[data.userId],
          userId: data.userId,
          status: data.status as any,
        }
      }));
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  }, [user]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }
  }, []);

  // Update status
  const updateStatus = useCallback(async (status: 'online' | 'away' | 'busy' | 'offline', autoStatus?: boolean) => {
    try {
      await api.put('/api/chat/presence/status', { status, autoStatus });
      if (socketRef.current?.connected) {
        socketRef.current.emit('presence:status', { status, autoStatus });
      }
    } catch (error) {
      console.error('[Chat WS] Error updating status:', error);
    }
  }, []);

  // Send message
  const sendMessage = useCallback(async (channelId: string, text: string, replyTo?: string, attachmentUrl?: string, attachmentName?: string, attachmentSize?: number) => {
    try {
      const payload: any = {};
      
      // Only include text if it's a non-empty string
      if (text && typeof text === 'string' && text.trim()) {
        payload.text = text.trim();
      }
      
      // Only include attachment fields if attachmentUrl is provided
      if (attachmentUrl) {
        payload.attachmentUrl = attachmentUrl;
        if (attachmentName) {
          payload.attachmentName = attachmentName;
        }
        if (attachmentSize !== undefined) {
          payload.attachmentSize = attachmentSize;
        }
      }
      
      // Include replyTo if provided
      if (replyTo) {
        payload.replyTo = replyTo;
      }
      
      await api.post(`/api/chat/channels/${channelId}/messages`, payload);
    } catch (error) {
      console.error('[Chat WS] Error sending message:', error);
      throw error;
    }
  }, []);

  // Typing indicators
  const startTyping = useCallback((channelId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing:start', { channelId });
    }
  }, []);

  const stopTyping = useCallback((channelId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing:stop', { channelId });
    }
  }, []);

  // Event listener helper
  const on = useCallback((event: string, handler: (data: any) => void) => {
    if (!socketRef.current) return () => {};

    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event)!.add(handler);

    socketRef.current.on(event, handler);

    return () => {
      listenersRef.current.get(event)?.delete(handler);
      socketRef.current?.off(event, handler);
    };
  }, []);

  // Emit helper
  const emit = useCallback((event: string, data: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  // Load initial presence
  useEffect(() => {
    if (!user) return;

    const loadPresence = async () => {
      try {
        const response = await api.get('/api/chat/presence');
        const presenceMap: Record<string, UserPresence> = {};
        response.data.presence.forEach((p: any) => {
          presenceMap[p.userId] = p;
        });
        setPresence(presenceMap);
      } catch (error) {
        console.error('[Chat WS] Error loading presence:', error);
      }
    };

    loadPresence();
  }, [user]);

  // Do not auto-connect on login. Connection starts only when user opens Chat (see Chat.tsx).

  const value: ChatWebSocketContextType = {
    socket,
    isConnected,
    presence,
    connect,
    disconnect,
    updateStatus,
    sendMessage,
    startTyping,
    stopTyping,
    on,
    emit,
  };

  return (
    <ChatWebSocketContext.Provider value={value}>
      {children}
    </ChatWebSocketContext.Provider>
  );
};

